import { PerspectiveAttribute, PerspectiveValueType } from '@the-real-insight/tri-model';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { currentLocale } from '../components/numericFormat';
import FieldHistory, { FieldChange } from './FieldHistory';
import { confidenceOf } from './valueConfidence';
import { formatDuration } from './durationValue';
import { ReferenceText } from './referenceLabel';

/**
 * Showing a perspective's attributes read-only.
 *
 * The alignment is the point. Numbers are right-aligned so the digits of one column line up and can
 * be compared at a glance, and the unit sits in a column of its own to the right of them — the
 * number ends at the same x on every row, and the units start at the same x. Putting the unit
 * directly after each number would ragged the very edge the eye reads down.
 */

/** Separators follow the reader's language, so the list and the input never disagree. */
const numberFormat = (valueType: string, locale: string): Intl.NumberFormat =>
    new Intl.NumberFormat(locale, {
        minimumFractionDigits: valueType === 'money' ? 2 : 0,
        maximumFractionDigits: valueType === 'integer' ? 0 : valueType === 'percent' ? 1 : 2,
        useGrouping: valueType === 'money' || valueType === 'integer',
    });

/** What a value with nothing in it reads as — including every derived value, for now. */
export const EMPTY = '–';

/**
 * Was rechtsbündig und mit Ziffernbreite steht — und was nicht.
 *
 * Die Ausrichtung ist der Punkt der Liste: Zahlen enden an derselben x-Position, damit sich eine
 * Spalte im Blick vergleichen lässt. Ein Ja, ein Behördenname oder „Zone II" gehört dort nicht hin;
 * rechtsbündig gesetzt zerrisse es genau die Kante, an der das Auge herunterliest.
 */
const NICHT_NUMERISCH = [
    PerspectiveValueType.text,
    PerspectiveValueType.date,
    PerspectiveValueType.boolean,
    PerspectiveValueType.selection,
    PerspectiveValueType.duration,
    PerspectiveValueType.reference,
    // Ein Objekt oder eine Liste wird zu einem Satz („Hochwasser (Flüsse) außerhalb …; Starkregen …").
    // In der Zahlenspalte drückte er die Beschriftung auf null Breite.
    PerspectiveValueType.object,
    PerspectiveValueType.list,
];

export const isNumeric = (attribute: PerspectiveAttribute): boolean =>
    !NICHT_NUMERISCH.includes(attribute.valueType);

/** Die Beschriftung eines Auswahlwerts — oder der gespeicherte Code, wenn er nicht in der Liste steht. */
const optionLabel = (attribute: PerspectiveAttribute, raw: any, lang: 'de' | 'en'): string => {
    const options = (attribute as { options?: { name: string; label: { de: string; en: string } }[] }).options;
    const found = options?.find((option) => option.name === raw);

    // Der Code selbst, wo die Liste ihn nicht kennt. Ein Strich verschwiege, dass etwas gespeichert
    // ist — und genau dann will man sehen, was.
    return found ? found.label[lang] ?? found.label.de : String(raw);
};

export const formatValue = (attribute: PerspectiveAttribute, raw: any, lang: 'de' | 'en' = 'de'): string => {
    // Eine Null ist eine Zahl und ein Falsch eine Antwort — beide dürfen nicht als „leer" gelten.
    if (raw === null || raw === undefined || raw === '') {
        return EMPTY;
    }

    if (attribute.valueType === PerspectiveValueType.date) {
        const parsed = new Date(raw);
        return Number.isNaN(parsed.getTime()) ? EMPTY : parsed.toLocaleDateString(currentLocale());
    }

    if (attribute.valueType === PerspectiveValueType.boolean) {
        const yes = lang === 'en' ? 'Yes' : 'Ja';
        const no = lang === 'en' ? 'No' : 'Nein';

        return raw === true || raw === 'true' ? yes : no;
    }

    if (attribute.valueType === PerspectiveValueType.selection) {
        return optionLabel(attribute, raw, lang);
    }

    // Ein Objekt: seine belegten Felder, jedes nach seinem Typ — „GRZ 0,4 · GFZ 1,2".
    if (attribute.valueType === PerspectiveValueType.object) {
        const fields = ((attribute as any).fields ?? []) as any[];
        const shown = fields
            .filter((field) => raw?.[field.name] !== null && raw?.[field.name] !== undefined && raw?.[field.name] !== '')
            .map((field) => `${field.label?.[lang] ?? field.label?.de ?? field.name} ${formatValue(field, raw[field.name], lang)}`);
        return shown.length > 0 ? shown.join(' · ') : EMPTY;
    }

    // Eine Liste: wie viele Einträge, und die ersten davon.
    if (attribute.valueType === PerspectiveValueType.list) {
        const items = Array.isArray(raw) ? raw : [];
        if (items.length === 0) return EMPTY;
        const item = (attribute as any).item ?? { valueType: PerspectiveValueType.text };
        const one = (entry: any) =>
            item.valueType === PerspectiveValueType.object
                ? formatValue({ ...item, label: attribute.label } as any, entry, lang)
                : item.valueType === PerspectiveValueType.reference
                  ? formatValue({ valueType: PerspectiveValueType.reference } as any, entry?.ref, lang)
                  : formatValue(item, entry?.value ?? entry, lang);
        const head = items.slice(0, 3).map(one).join('; ');
        return items.length > 3 ? `${head} … (${items.length})` : head;
    }

    // Eine typisierte Referenz ohne nachgeschlagenen Namen: Typ und Ende der Kennung, ehrlich statt leer.
    if (attribute.valueType === PerspectiveValueType.reference && raw && typeof raw === 'object') {
        return `${raw.type ?? ''} ${String(raw._id ?? '').slice(-6)}`.trim();
    }

    // A span in years and months reads as such; any other stored form is shown as it is.
    if (attribute.valueType === PerspectiveValueType.duration) {
        return formatDuration(raw, lang) ?? String(raw);
    }

    if (
        attribute.valueType === PerspectiveValueType.text ||
        // Ein Verweis liegt als Kennung des Objekts vor. Bis es dafür ein Nachschlagen gibt, steht
        // die Kennung da — sie ist unschön, aber sie ist wahr, und ein Strich wäre es nicht.
        attribute.valueType === PerspectiveValueType.reference
    ) {
        return String(raw);
    }

    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) {
        return EMPTY;
    }

    return numberFormat(attribute.valueType, currentLocale()).format(numeric);
};

interface Properties {
    attributes: PerspectiveAttribute[];
    values: Record<string, any> | undefined;
    /** Show only the first n rows — a tile has less room than the drill-down. */
    limit?: number;
    /**
     * How many of the phase's checklist tasks are still open, for the point that is filled from
     * them. Undefined while they are unknown, which reads as a dash like any other missing value.
     */
    openChecklistTasks?: number;
}

/**
 * The read-only list of a perspective's attributes.
 *
 * A derived attribute shows a dash: the computation that would fill it does not exist yet, and
 * leaving the row out entirely would hide that the perspective has that figure at all.
 */
/**
 * Points that only the drill-down shows. A graph of companies or a plan of dates has no one-line
 * form a tile could hold, and a dash in their place would claim they were empty.
 */
const DRILL_DOWN_ONLY = [PerspectiveValueType.organizationChart, PerspectiveValueType.milestones];

/**
 * Der Wert eines Slots, wie die Übersicht ihn zeigt: bei einem gebundenen Slot der Wert an der Instanz
 * (`resolved`, vom Server mitgeliefert) — bei `many` steht an der Perspektive selbst nichts, die Werte
 * stehen an den Instanzen. Sonst, was an der Perspektive steht.
 */
export const shownValue = (attribute: PerspectiveAttribute, values: Record<string, any> | null | undefined): { raw: any; many?: any[] } => {
    const bound = values?.resolved?.[attribute.name];
    const present = (bound?.instances ?? []).filter(
        (instance: any) => !instance.missing && instance.value !== null && instance.value !== undefined && instance.value !== '',
    );
    if (bound && present.length > 0) {
        return bound.cardinality === 'one' ? { raw: present[0].value } : { raw: present[0].value, many: present.map((instance: any) => instance.value) };
    }
    return { raw: values?.[attribute.name] };
};

/** Ein einzelner Bezug, dessen Bezeichnung die Übersicht nachschlägt (nicht bei mehreren Werten). */
const referenceOf = (attribute: PerspectiveAttribute, values: Record<string, any> | null | undefined): { _id: string; type: string } | null => {
    if (attribute.valueType !== PerspectiveValueType.reference) return null;
    const shown = shownValue(attribute, values);
    const raw = shown.many && shown.many.length > 1 ? null : shown.raw;
    return raw && typeof raw === 'object' && raw._id && raw.type ? { _id: String(raw._id), type: String(raw.type) } : null;
};

const formatShown = (attribute: PerspectiveAttribute, shown: { raw: any; many?: any[] }, lang: 'de' | 'en'): string =>
    shown.many && shown.many.length > 1
        ? Array.from(new Set(shown.many.map((value) => formatValue(attribute, value, lang)))).join('; ')
        : formatValue(attribute, shown.raw, lang);

export default function PerspectiveValueList({ attributes, values, limit, openChecklistTasks }: Properties) {
    const { i18n } = useTranslation();
    const listed = attributes.filter((attribute) => !DRILL_DOWN_ONLY.includes(attribute.valueType));
    const shown = limit ? listed.slice(0, limit) : listed;
    const lang = i18n.language.startsWith('en') ? 'en' : 'de';

    return (
        <div className="grid grid-cols-[max-content_minmax(0,1fr)_max-content_max-content] items-baseline gap-x-2">
            {shown.map((attribute) => {
                const checklistPoint = attribute.valueType === PerspectiveValueType.checklists;
                // The parcel list is a table in the drill-down; on a tile there is no room, and the
                // count beside it already says how many there are.
                const parcelPoint = attribute.valueType === PerspectiveValueType.parcels;
                // A derived value is shown like any other once something has derived it — the site
                // estimator writes the plot size and the parcel count, and a dash where a figure
                // stands would hide the very thing that was worked out. Empty still reads as a
                // dash, which is what `formatValue` does with nothing.
                const text = checklistPoint
                    ? openChecklistTasks === undefined
                        ? EMPTY
                        : String(openChecklistTasks)
                    : parcelPoint
                      ? EMPTY
                      : formatShown(attribute, shownValue(attribute, values), lang as 'de' | 'en');
                const numeric = isNumeric(attribute) && !checklistPoint && !parcelPoint;
                const label = attribute.label[lang as 'en' | 'de'] ?? attribute.label.de;

                // The two table points stand for a list rather than a value; a dot claiming they
                // are "not set" would say something false about a count of open tasks.
                const confidence =
                    checklistPoint || parcelPoint
                        ? null
                        : confidenceOf(attribute, shownValue(attribute, values).raw, values?.changes as FieldChange[]);

                return (
                    <div key={attribute.name} className="contents">
                        {/* The dot opens the history here too. Its click is kept from the tile
                            underneath, which would otherwise open the perspective at the same
                            moment — two things happening on one press, and only one of them
                            asked for. */}
                        <span
                            className="flex h-full items-center py-0.5"
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                            role="presentation"
                        >
                            {confidence ? (
                                <FieldHistory
                                    // This field's entries only: a perspective keeps one list for
                                    // all of them, and the popover is about one row.
                                    changes={((values?.changes as FieldChange[] | undefined) ?? []).filter(
                                        (entry) => entry.field === attribute.name,
                                    )}
                                    confidence={confidence}
                                    format={(value) => formatValue(attribute, value, lang as 'de' | 'en')}
                                />
                            ) : (
                                <></>
                            )}
                        </span>
                        <span className="truncate py-0.5 text-xs leading-relaxed text-muted-foreground" title={label}>
                            {label}
                        </span>
                        <span
                            // Abgeschnitten ist unvermeidbar — der volle Wert beim Darüberfahren.
                            title={referenceOf(attribute, values) ? undefined : text}
                            className={cn(
                                'truncate py-0.5 text-sm',
                                numeric
                                    ? 'max-w-[140px] text-right font-bold tabular-nums'
                                    : 'col-span-2 max-w-[190px] font-semibold',
                            )}
                        >
                            {referenceOf(attribute, values) ? <ReferenceText reference={referenceOf(attribute, values)!} fallback={text} /> : text}
                        </span>
                        {numeric ? (
                            <span className="whitespace-nowrap py-0.5 text-left text-xs text-muted-foreground">
                                {attribute.unit ?? ''}
                            </span>
                        ) : (
                            <></>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
